// Project Manager → Team (/pm/team).
//
// A PM staffs their own projects, so they invite and manage the people who do
// the work: testers and developers. They cannot appoint another project manager
// or touch an Admin — the server enforces both, and returns 404 (not 403) on an
// Admin so a PM can't discover who the admins are by probing.
//
// Seats: only TESTERS consume a paid seat. Developers and PMs are free, which
// surprises people often enough that the breakdown is on the page rather than in
// a doc nobody reads.

import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  UserPlus, Users, Loader2, Trash2, Send, Copy, Check, AlertTriangle,
  CheckCircle2, Code2, FlaskConical, Clock,
} from "lucide-react";
import PageHeader from "@/components/dashboard/PageHeader";
import DataTable from "@/components/dashboard/DataTable";
import Modal from "@/components/dashboard/Modal";
import ConfirmDialog from "@/components/dashboard/ConfirmDialog";
import Input from "@/components/ui/Input";
import Button from "@/components/ui/Button";
import { Badge } from "@/components/ui/Badge";
import ConfirmWithPassword from "@/components/shared/ConfirmWithPassword";
import { useApiData } from "@/hooks/useApiData";
import { listTeam, inviteMember, removeTester, resendInvite, updateTester } from "@/api/company";
import { cn } from "@/lib/cn";
import { validateFullName } from "@/lib/validation";
import { formatDate } from "@/lib/dates";

const EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;
// A PM may staff their projects. Appointing another PM is an Admin's call.
const INVITABLE = [
  { value: "tester", label: "Tester", icon: FlaskConical,
    hint: "Runs the suite. Uses one paid seat." },
  { value: "developer", label: "Developer", icon: Code2,
    hint: "Reads reports and marks fixes. Free — no seat." },
];

const fmtDate = (v) => formatDate(v);

function initials(name, email) {
  const s = name || email || "?";
  return s.split(/[\s@.]+/).map((p) => p[0]).slice(0, 2).join("").toUpperCase();
}

function CopyBox({ value }) {
  const [copied, setCopied] = useState(false);
  return (
    <div className="flex items-stretch gap-2">
      <code className="flex-1 break-all rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 font-mono text-xs text-slate-800 dark:border-white/10 dark:bg-white/5 dark:text-slate-100">
        {value}
      </code>
      <button
        type="button"
        onClick={() => {
          navigator.clipboard?.writeText(value);
          setCopied(true);
          setTimeout(() => setCopied(false), 1500);
        }}
        className="inline-flex shrink-0 items-center gap-1.5 rounded-lg bg-brand-600 px-3 text-sm font-semibold text-white hover:bg-brand-500"
      >
        {copied ? <Check size={15} /> : <Copy size={15} />} {copied ? "Copied" : "Copy"}
      </button>
    </div>
  );
}

// Deactivated members are requested explicitly: the Deactivate/Reactivate toggle
// below is unusable without them, because deactivating drops the row out of the
// default (live-only) list along with the button that would bring them back.
const loadTeam = () => listTeam({ includeInactive: true });

export default function PmTeam() {
  const { data, loading, error: loadError, reload } = useApiData(loadTeam, []);
  const team = useMemo(
    () => (Array.isArray(data) ? data : []).filter(
      (u) => u.role === "tester" || u.role === "developer",
    ),
    [data],
  );

  const [invite, setInvite] = useState(false);
  const [form, setForm] = useState({ email: "", name: "", role: "tester" });
  const [formError, setFormError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);          // { email, accept_url, email_sent }
  const [confirmDel, setConfirmDel] = useState(null);
  const [delError, setDelError] = useState("");
  // The member awaiting a Deactivate confirmation. Deactivating cuts someone's
  // sign-in on the next click of a button that sits inches from Remove, so it
  // gets the same confirmation step every other destructive action on this
  // surface has (ConfirmDialog — the one PmProjects already uses). Reactivating
  // is NOT destructive and stays a single click.
  const [confirmDeactivate, setConfirmDeactivate] = useState(null);
  const [toast, setToast] = useState(null);
  // One owned timer. Each toast used to start an uncleared setTimeout, so a second
  // toast was cut short by the first one's timer and the last fired after unmount.
  const toastTimer = useRef(null);

  const showToast = useCallback((message) => {
    setToast(message);
    if (toastTimer.current) clearTimeout(toastTimer.current);
    toastTimer.current = setTimeout(() => setToast(null), 4000);
  }, []);

  useEffect(() => () => { if (toastTimer.current) clearTimeout(toastTimer.current); }, []);

  const testers = team.filter((u) => u.role === "tester");
  const developers = team.filter((u) => u.role === "developer");
  const pending = team.filter((u) => u.invite_status === "pending");

  function closeInvite() {
    setInvite(false);
    setForm({ email: "", name: "", role: "tester" });
    setFormError(null);
    setResult(null);
  }

  async function submit() {
    const email = form.email.trim().toLowerCase();
    const name = form.name.trim();
    // Validate here as well as server-side: a round-trip to be told "that's not
    // an email" is a worse experience than being told before you send it.
    if (!email) return setFormError("An email address is required.");
    if (!EMAIL_RE.test(email)) return setFormError("That doesn't look like an email address.");
    if (email.length > 255) return setFormError("That email is too long (max 255 characters).");
    // The name stays OPTIONAL, but anything typed has to actually be a name.
    // validateFullName is the rule signup, invite-acceptance and the profile page
    // already share, and it mirrors _validated_person_name on the API — which is
    // the control. It is unicode-aware on purpose ("José", "李伟" are names); what
    // it rejects is digits and symbols, which used to be stored verbatim and then
    // shown on every team list, report and invitation email.
    const nameError = name ? validateFullName(name) : "";
    if (nameError) return setFormError(nameError);

    setBusy(true); setFormError(null);
    try {
      const res = await inviteMember({ email, name: name || null, role: form.role });
      // email_sent is reported by the server and used to be discarded, so a failed
      // delivery still claimed "We emailed an invitation".
      setResult({ email, accept_url: res?.accept_url, email_sent: res?.email_sent !== false });
      await reload();
    } catch (e) {
      setFormError((e && e.message) || "Could not send that invitation.");
    } finally {
      setBusy(false);
    }
  }

  async function resend(member) {
    setBusy(true);
    try {
      const res = await resendInvite(member.id);
      showToast(res?.email_sent === false
        ? `We couldn't email ${member.email} — open Invite to copy their link instead.`
        : `Invitation re-sent to ${member.email}.`);
    } catch (e) {
      showToast((e && e.message) || "Could not re-send that invitation.");
    } finally {
      setBusy(false);
    }
  }

  async function toggleActive(member) {
    setBusy(true);
    try {
      await updateTester(member.id, { is_active: !member.is_active });
      await reload();
    } catch (e) {
      showToast((e && e.message) || "Could not update that member.");
    } finally {
      setBusy(false);
    }
  }

  // Confirm → deactivate. The request is only sent from here, so Cancel (and the
  // dialog's Escape/backdrop close) never reaches the API. `busy` disables the
  // confirm button for the duration, so a double-click can't send it twice.
  async function confirmDeactivateMember() {
    const member = confirmDeactivate;
    if (!member || busy) return;
    await toggleActive(member);
    setConfirmDeactivate(null);
  }

  async function remove(password) {
    setBusy(true);
    setDelError("");
    try {
      await removeTester(confirmDel.id, password);
      setConfirmDel(null);
      await reload();
    } catch (e) {
      // Wrong password → keep the dialog up so it can be corrected.
      setDelError((e && e.message) || "Could not remove that member.");
    } finally {
      setBusy(false);
    }
  }

  const rows = useMemo(() => team.map((u) => ({
    ...u,
    // Order matters: an invited user has is_active=false AND invite_status
    // "pending". Checking is_active first labels every new invite "Inactive",
    // which reads as "we disabled them".
    state: u.invite_status === "pending" ? "pending" : (u.is_active ? "active" : "inactive"),
    joined: fmtDate(u.created_at),
  })), [team]);

  const columns = [
    {
      key: "name", header: "Member", sortable: true,
      render: (r) => (
        <div className="flex min-w-0 items-center gap-3">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-slate-100 text-xs font-semibold text-slate-600 dark:bg-white/10 dark:text-slate-200">
            {initials(r.name, r.email)}
          </span>
          <div className="min-w-0">
            <p className="truncate font-medium text-slate-800 dark:text-slate-100">
              {r.name || r.email}
            </p>
            <p className="truncate text-xs text-slate-400">{r.email}</p>
          </div>
        </div>
      ),
    },
    {
      key: "role", header: "Role", sortable: true,
      render: (r) => (
        <Badge tone={r.role === "tester" ? "brand" : "slate"} className="capitalize">
          {r.role}
        </Badge>
      ),
    },
    {
      key: "state", header: "Status", sortable: true,
      render: (r) => (
        r.state === "pending" ? (
          <Badge tone="amber"><Clock size={11} /> Invite pending</Badge>
        ) : r.state === "active" ? (
          <Badge tone="emerald"><CheckCircle2 size={11} /> Active</Badge>
        ) : (
          <Badge tone="slate">Deactivated</Badge>
        )
      ),
    },
    { key: "joined", header: "Added", sortable: true },
    {
      key: "actions", header: "", align: "right",
      render: (r) => (
        <div className="flex justify-end gap-1" onClick={(e) => e.stopPropagation()}>
          {r.state === "pending" && (
            <Button variant="outline" size="sm" onClick={() => resend(r)} disabled={busy}>
              <Send size={14} /> Resend
            </Button>
          )}
          {r.state !== "pending" && (
            <Button
              variant="outline" size="sm" disabled={busy}
              /* Deactivating asks first; reactivating restores access and needs
                 no confirmation step. */
              onClick={() => (r.is_active ? setConfirmDeactivate(r) : toggleActive(r))}
            >
              {r.is_active ? "Deactivate" : "Reactivate"}
            </Button>
          )}
          <button
            type="button"
            onClick={() => setConfirmDel(r)}
            title="Remove from the company"
            className="grid h-9 w-9 place-items-center rounded-lg text-slate-400 hover:bg-rose-50 hover:text-rose-600 dark:hover:bg-rose-500/10"
          >
            <Trash2 size={15} />
          </button>
        </div>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title="Team"
        subtitle="The people you can put on a project — testers run the suite, developers fix what it finds."
        actions={
          <Button size="sm" onClick={() => { setResult(null); setFormError(null); setInvite(true); }}>
            <UserPlus size={16} /> Invite someone
          </Button>
        }
      />

      {toast && (
        <div className="mb-4 rounded-xl bg-slate-900 px-4 py-3 text-sm text-white dark:bg-white/10">
          {toast}
        </div>
      )}

      {/* Seat breakdown — developers are free, and people assume otherwise. */}
      <div className="mb-5 grid gap-3 sm:grid-cols-3">
        <div className="card px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Testers</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{testers.length}</p>
          <p className="text-xs text-slate-400">Each uses one paid seat</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Developers</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{developers.length}</p>
          <p className="text-xs text-slate-400">Free — no seat used</p>
        </div>
        <div className="card px-4 py-3">
          <p className="text-xs font-medium uppercase tracking-wide text-slate-400">Awaiting acceptance</p>
          <p className="mt-1 text-2xl font-bold text-slate-900 dark:text-white">{pending.length}</p>
          <p className="text-xs text-slate-400">Invitations not yet accepted</p>
        </div>
      </div>

      {loading && !data ? (
        /* Only the FIRST load takes over the page. Every mutation awaits reload(),
           which flips loading true again — unmounting DataTable and silently
           wiping the search box, filters, sort and page. */
        <div className="flex justify-center py-16 text-slate-400">
          <Loader2 size={22} className="animate-spin" />
        </div>
      ) : loadError ? (
        /* Distinct from the empty state: a failed request used to render "Nobody
           on the team yet", which reads as a fact about the company. */
        <div className="card p-12 text-center">
          <AlertTriangle size={26} className="mx-auto mb-3 text-amber-500" />
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">
            Couldn&apos;t load your team
          </h3>
          <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500 dark:text-slate-400">{loadError}</p>
          <Button className="mt-6" variant="outline" onClick={reload}>Try again</Button>
        </div>
      ) : rows.length === 0 ? (
        <div className="card p-12 text-center">
          <span className="mx-auto mb-4 grid h-14 w-14 place-items-center rounded-2xl bg-brand-500/10 text-brand-600 dark:text-brand-300">
            <Users size={26} />
          </span>
          <h3 className="font-display text-lg font-semibold text-slate-900 dark:text-white">
            Nobody on the team yet
          </h3>
          <p className="mx-auto mt-2 max-w-sm text-sm text-slate-500 dark:text-slate-400">
            Invite a tester to run your suites and a developer to fix what they find.
          </p>
          <Button className="mt-6" onClick={() => setInvite(true)}>
            <UserPlus size={16} /> Invite someone
          </Button>
        </div>
      ) : (
        <DataTable
          columns={columns}
          rows={rows}
          searchKeys={["name", "email"]}
          filters={[
            { key: "role", label: "roles", options: ["tester", "developer"] },
            { key: "state", label: "statuses", options: ["active", "pending", "inactive"] },
          ]}
          pageSize={15}
          maxHeight="34rem"
          emptyLabel="Nobody matches those filters."
        />
      )}

      {/* invite ------------------------------------------------------------ */}
      <Modal
        open={invite}
        /* Not closable mid-request: Escape/backdrop/Cancel cleared `result`, then
           the in-flight promise resolved and set it again — so the next time the
           PM opened Invite they were greeted by a stale "Invitation sent". */
        onClose={busy ? undefined : closeInvite}
        title={result ? "Invitation sent" : "Invite someone"}
        footer={
          <div className="flex justify-end gap-2">
            {result ? (
              <Button size="sm" onClick={closeInvite}>Done</Button>
            ) : (
              <>
                <Button variant="outline" size="sm" onClick={closeInvite} disabled={busy}>Cancel</Button>
                <Button size="sm" onClick={submit} disabled={busy}>
                  {busy ? <Loader2 size={15} className="animate-spin" /> : "Send invitation"}
                </Button>
              </>
            )}
          </div>
        }
      >
        {result ? (
          <div className="space-y-4">
            {result.email_sent ? (
              <div className="flex items-start gap-2.5 rounded-xl bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:bg-emerald-500/10 dark:text-emerald-200">
                <CheckCircle2 size={16} className="mt-0.5 shrink-0" />
                <span>We emailed an invitation to <strong>{result.email}</strong>.</span>
              </div>
            ) : (
              <div className="flex items-start gap-2.5 rounded-xl bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-500/10 dark:text-amber-200">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>
                  <strong>{result.email}</strong> was added, but we couldn&apos;t send the
                  email. Share the link below with them directly.
                </span>
              </div>
            )}
            {result.accept_url && (
              <div>
                <p className="mb-2 text-xs text-slate-500 dark:text-slate-400">
                  {result.email_sent
                    ? "If the email doesn’t arrive, send them this link directly:"
                    : "Their invitation link:"}
                </p>
                <CopyBox value={result.accept_url} />
              </div>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {formError && (
              <div className="flex items-start gap-2.5 rounded-xl bg-rose-50 px-4 py-3 text-sm text-rose-700 dark:bg-rose-500/10 dark:text-rose-300">
                <AlertTriangle size={16} className="mt-0.5 shrink-0" />
                <span>{formError}</span>
              </div>
            )}

            <div>
              <p className="mb-2 text-sm font-medium text-slate-700 dark:text-slate-200">
                What will they do?
              </p>
              <div className="grid gap-2 sm:grid-cols-2">
                {INVITABLE.map((r) => (
                  <button
                    key={r.value}
                    type="button"
                    onClick={() => setForm({ ...form, role: r.value })}
                    className={cn(
                      "rounded-xl border px-4 py-3 text-left transition-colors",
                      form.role === r.value
                        ? "border-brand-400 bg-brand-50/70 dark:border-brand-500/40 dark:bg-brand-500/10"
                        : "border-slate-200 hover:bg-slate-50 dark:border-white/10 dark:hover:bg-white/5",
                    )}
                  >
                    <span className="flex items-center gap-2 text-sm font-semibold text-slate-800 dark:text-slate-100">
                      <r.icon size={15} /> {r.label}
                    </span>
                    <span className="mt-1 block text-xs text-slate-500 dark:text-slate-400">
                      {r.hint}
                    </span>
                  </button>
                ))}
              </div>
            </div>

            <Input
              /* Required marker: the same `<span className="text-rose-500"> *</span>`
                 the assistant contact form and the exe import mapper already use.
                 Input renders `label` as a node, so this needs no new component
                 and keeps the existing htmlFor/aria wiring. */
              label={<>Email address<span className="text-rose-500"> *</span></>}
              aria-required="true"
              placeholder="name@company.com"
              value={form.email}
              onChange={(e) => setForm({ ...form, email: e.target.value })}
            />
            <Input
              label="Name (optional)"
              placeholder="How should we address them?"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
            />
            <p className="rounded-xl bg-slate-50 px-4 py-3 text-xs leading-relaxed text-slate-500 dark:bg-white/5 dark:text-slate-400">
              Need another project manager, or an admin? Only a company admin can
              appoint those.
            </p>
          </div>
        )}
      </Modal>

      {/* deactivate — confirm before the mutation, never after */}
      <ConfirmDialog
        open={!!confirmDeactivate}
        title="Deactivate this member?"
        body={
          `${confirmDeactivate?.name || confirmDeactivate?.email || "This member"} will be `
          + "signed out and blocked from signing in again. Their history and project "
          + "assignments are kept, and you can reactivate them at any time."
        }
        confirmLabel="Deactivate"
        busy={busy}
        onConfirm={confirmDeactivateMember}
        onCancel={() => setConfirmDeactivate(null)}
      />

      {/* remove — re-authenticates before cutting someone's access */}
      <ConfirmWithPassword
        open={!!confirmDel}
        title="Remove from the company?"
        confirmLabel="Remove"
        error={delError}
        busy={busy}
        onClose={() => { setConfirmDel(null); setDelError(""); }}
        onConfirm={remove}
      >
        <p><strong>{confirmDel?.name || confirmDel?.email}</strong> loses access
          immediately, comes off every project, and any outstanding invitation
          link stops working.</p>
        <p className="mt-1 opacity-90">Their past work and the activity log entry for it stay.</p>
      </ConfirmWithPassword>
    </>
  );
}
